/**
 * What a plan will be built by, and how long it takes.
 *
 * THE LEAD TIME IS THE ONE PIECE OF ARITHMETIC THE MANUFACTURING PROGRAMME ADDS. Everything else in
 * Phases 5 to 7 records something somebody already knew; this derives a number nobody has. It is
 * also the reason Phase 5 built shift configurations and process steps at all, which is why this
 * pane puts it at the top rather than in a corner under the table.
 *
 * NOTHING ABOUT IT IS STORED, AND THE PANE SAYS SO. The source product keeps `lead_time_days` as a
 * column filled from an MRP integration this programme excludes; here it would be a column nothing
 * maintained, and a stored lead time that disagrees with the steps underneath it is Phase 6's
 * stored-total defect in a different hat. So it is computed on every read, and this component does
 * not recompute it either: the steps read returns the schedule WITH the rows, so there is never a
 * frame where a route and its lead time disagree.
 *
 * THE FOUR CAVEATS ARE SHOWN, NOT BURIED. `StepsWithoutHours`, `ManualSteps`, `ShiftConfigured` and
 * `CalendarDaysNotWorkingDays` each travel with the number because each is a reason to trust it
 * less, and three of them have already been the subject of a wrong assumption in this estate. A
 * lead time of 14 days over a plan with four unmeasured steps is not a 14-day plan, and a reader
 * who cannot see that has been misled by a number that looks exact.
 *
 * THE DIVERGENCE IS THE OTHER THING THIS PANE EXISTS FOR. A plan starts as a copy of the item's
 * routing and is then edited; `FromRouting` says which steps came from it. A plan where most steps
 * were added by hand no longer follows the item's manufacturing definition, and NOTHING ELSE on the
 * record would say so -- §2.3 chose capture-over-pointing precisely to make that visible, and a
 * surface that did not show it would waste the decision.
 *
 * READ-ONLY OUTSIDE DRAFT, like the quote lines and for the same reason: re-planning happens in a
 * new revision, so an approved plan's route is what somebody approved.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {
    Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, IconButton, MenuItem, TextField, Tooltip, Typography,
} from "@mui/material";
import {DeleteOutlined, EditOutlined, PersonOutlined} from "@mui/icons-material";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Blue3, Grey2, Grey5, Grey7, Grey8, Grey17} from "../../theme/styles";
import {FormObjectPicker} from "../Forms/FormObjectPicker";

// THE FOLDER'S VERB, since 2026-10-27; "buildPlan:edit" was retired that morning and this pane kept asking
// for it, so every build plan was read-only on screen. See QuoteLinesPane.
const EDIT_PRIVILEGE = "project:edit";

/** The service's own list. A fifth value is refused there, not here. */
const STEP_STATUSES = ["PLANNED", "IN_PROGRESS", "COMPLETE", "SKIPPED"];

type Step = {
    USID: string;
    Name?: string | null;
    Description?: string | null;
    ProcessStepUSID?: string | null;
    FromRouting?: boolean;
    EquipmentObjectUSID?: string | null;
    HoursPerUnit?: string | null;
    OvertimeHours?: string | null;
    SortOrder?: number;
    Status?: string | null;
    Notes?: string | null;
    People?: any[];
};

type Schedule = {
    Quantity?: string | null;
    Steps?: number;
    StepsNotCounted?: number;
    HoursPerUnit?: string | null;
    NormalHours?: string | null;
    OvertimeHours?: string | null;
    OvertimeCostMultiplier?: string | null;
    EquivalentNormalHours?: string | null;
    ShiftSlots?: number | null;
    MaxDailyHours?: string | null;
    CapacityHoursPerDay?: string | null;
    LeadTimeDays?: number | null;
    PlanStartDate?: string | null;
    PlannedEndDate?: string | null;
    NeedByDate?: string | null;
    MeetsNeedByDate?: boolean | null;
    SlackDays?: number | null;
    StepsWithoutHours?: number;
    ManualSteps?: number;
    ShiftConfigured?: boolean;
    CalendarDaysNotWorkingDays?: boolean;
};

type Terms = {
    Quantity?: string | null;
    UnitOfMeasure?: string | null;
    NeedByDate?: string | null;
    PlanStartDate?: string | null;
    Notes?: string | null;
    ItemObjectUSID?: string | null;
    PreparedQuoteRevisionUSID?: string | null;
};

function when(value?: string | null): string {
    if (!value) return "—";
    const at = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 10);
    return at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

/** Hours as a person says them: 7.5, not 7.500000. Never through a float -- see quoteMoney. */
export function hours(value?: string | null): string {
    if (value === null || value === undefined || value === "") return "—";
    const [whole, fraction = ""] = String(value).split(".");
    const trimmed = fraction.replace(/0+$/, "");
    return trimmed ? `${whole}.${trimmed}` : whole;
}

const COLUMNS = "28px minmax(160px, 3fr) 110px 100px 90px 110px 64px 72px";

const headerCell: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: Grey7, textTransform: "uppercase",
    letterSpacing: "0.04em",
};

export default function BuildPlanPane(
    {revisionUSID, readOnly, status}:
    {revisionUSID?: string | null; readOnly?: boolean; status?: string | null}) {

    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [steps, setSteps] = useState<Step[] | null>(null);
    const [schedule, setSchedule] = useState<Schedule | null>(null);
    const [divergence, setDivergence] = useState<{from: number; byHand: number} | null>(null);
    const [terms, setTerms] = useState<Terms | null>(null);
    const [needsTerms, setNeedsTerms] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [mayEdit, setMayEdit] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);
    const [stepDialog, setStepDialog] = useState<Step | "new" | null>(null);
    const [termsDialog, setTermsDialog] = useState(false);

    const editable = !readOnly && mayEdit;

    const load = useCallback(() => {
        if (!revisionUSID) return;
        setLoading(true);
        api.get("t1/v1/build_plan_steps", {params: {BuildPlanRevisionUSID: revisionUSID}})
            .then((response: any) => {
                const message = response?.data?.PlatformMessage ?? {};
                const held = message?.ListOfBuildPlanStep?.BuildPlanStep ?? [];
                setSteps(Array.isArray(held) ? held : [held]);
                setSchedule(message?.Schedule ?? null);
                setDivergence({from: message?.StepsFromRouting ?? 0,
                               byHand: message?.StepsAddedByHand ?? 0});
                setNeedsTerms(false);
                setError(null);
            })
            .catch((problem: any) => {
                // THE FIRST-RUN STATE, TOLD APART BY ITS CODE. A plan with no quantity has no
                // schedule, and there is no sensible default -- 1 would quote a 250-unit job as a
                // single unit and be wrong by two orders of magnitude without looking wrong.
                if (problem?.response?.data?.Code === "CC-BPLN-TERMS") {
                    setNeedsTerms(true);
                    setSteps([]);
                    setSchedule(null);
                    setError(null);
                    return;
                }
                setSteps(null);
                setSchedule(null);
                setError(problem?.response?.data?.Cause ?? "This plan's steps could not be read.");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionUSID]);

    const loadTerms = useCallback(() => {
        if (!revisionUSID) return;
        api.get("t1/v1/build_plan_terms", {params: {BuildPlanRevisionUSID: revisionUSID}})
            .then((response: any) => setTerms(response?.data?.PlatformMessage?.BuildPlanTerms
                                              ?? response?.data?.PlatformMessage ?? null))
            .catch(() => setTerms(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionUSID]);

    useEffect(() => { load(); loadTerms(); }, [load, loadTerms]);

    useEffect(() => {
        if (!revisionUSID) return;
        let cancelled = false;
        api.get("t1/v1/object_access", {params: {USID: revisionUSID}})
            .then((response: any) => {
                if (cancelled) return;
                const granted: string[] = response?.data?.EffectivePrivileges ?? [];
                setMayEdit(granted.includes(EDIT_PRIVILEGE));
            })
            .catch(() => { if (!cancelled) setMayEdit(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionUSID]);

    const remove = async (step: Step) => {
        setBusy(step.USID);
        try {
            await api.delete("t1/v1/build_plan_steps", {params: {USID: step.USID}});
            load();
        } catch (problem: any) {
            setError(problem?.response?.data?.Cause ?? "That step could not be removed.");
        } finally {
            setBusy(null);
        }
    };

    if (loading && steps === null && !error) {
        return <Box sx={{display: "flex", gap: "10px", alignItems: "center", padding: "20px 0"}}>
            <CircularProgress size={18}/>
            <Typography sx={{fontSize: 13, color: Grey7}}>Reading the plan…</Typography>
        </Box>;
    }

    return (
        <Box sx={{paddingTop: "6px", maxWidth: 1020}}>

            {/* ── what is being built ───────────────────────────────────────────────────────── */}
            <Box sx={{display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8}}>
                    What this plan builds
                </Typography>
                <Box sx={{flex: 1}}/>
                {editable && (
                    <Button size="small" variant="outlined" onClick={() => setTermsDialog(true)}>
                        {needsTerms ? "Set the quantity" : "Change"}
                    </Button>
                )}
            </Box>

            {needsTerms ? (
                <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "14px 16px",
                          marginBottom: "18px"}}>
                    <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 700}}>
                        This revision has no quantity yet, so it has no schedule. There is no
                        sensible default for it — planning a 250-unit job as a single unit would be
                        wrong by two orders of magnitude without looking wrong — so the quantity is
                        the first thing to set.
                    </Typography>
                    {!editable && (
                        <Typography sx={{fontSize: 13, color: Grey5, marginTop: "8px"}}>
                            {readOnly
                                ? "This revision is not a draft, so its terms cannot be set here."
                                : "You do not hold the privilege to plan this build."}
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box sx={{display: "flex", gap: "28px", flexWrap: "wrap",
                          border: `1px solid ${Grey2}`, borderRadius: "6px",
                          padding: "12px 16px", marginBottom: "18px"}}>
                    <Fact label="Quantity"
                          value={terms?.Quantity
                                 ? `${hours(terms.Quantity)}${terms.UnitOfMeasure
                                                              ? ` ${terms.UnitOfMeasure}` : ""}`
                                 : "—"}/>
                    <Fact label="Plan starts" value={when(terms?.PlanStartDate)}/>
                    <Fact label="Needed by" value={when(terms?.NeedByDate)}/>
                </Box>
            )}

            {/* ── the schedule, which is the number nobody else has ─────────────────────────── */}
            {schedule && <ScheduleBlock schedule={schedule}/>}

            {/* ── the route ─────────────────────────────────────────────────────────────────── */}
            <Box sx={{display: "flex", alignItems: "center", gap: "12px", marginTop: "20px",
                      marginBottom: "10px"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8}}>Steps</Typography>
                {!!steps?.length && (
                    <Typography sx={{fontSize: 13, color: Grey5}}>
                        {steps.length === 1 ? "1 step" : `${steps.length} steps`}
                    </Typography>
                )}
                <Box sx={{flex: 1}}/>
                {editable && !needsTerms && (
                    <Button size="small" variant="outlined" onClick={() => setStepDialog("new")}>
                        Add a step
                    </Button>
                )}
            </Box>

            {error && (
                <Typography sx={{fontSize: 13, color: "#B3261E", marginBottom: "10px"}}>
                    {error}
                </Typography>
            )}

            {readOnly && !needsTerms && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 700,
                                 marginBottom: "12px"}}>
                    This revision is {String(status ?? "").toLowerCase() || "not a draft"}, so its
                    route is what was approved and cannot be changed. To re-plan it, revise the
                    plan — that makes a draft to work in, and this revision stays as it was
                    approved.
                </Typography>
            )}

            {needsTerms && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 700}}>
                    Steps can be added once the plan has a quantity. Every step's hours are per
                    unit, so there is nothing to multiply them by until then.
                </Typography>
            )}

            {!error && !needsTerms && steps?.length === 0 && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 700}}>
                    This plan has no steps yet. A plan usually starts as a copy of the item's
                    routing and is then edited; steps added here are marked as added by hand, which
                    is how the plan reports that it no longer follows the item's definition.
                </Typography>
            )}

            {!!steps?.length && (
                <>
                    <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px",
                              overflow: "hidden"}}>
                        <Box sx={{display: "grid", gridTemplateColumns: COLUMNS, gap: "12px",
                                  padding: "10px 14px", borderBottom: `1px solid ${Grey2}`,
                                  background: "#FAFAFA"}}>
                            <Typography sx={headerCell}>#</Typography>
                            <Typography sx={headerCell}>Step</Typography>
                            <Typography sx={headerCell}>Source</Typography>
                            <Typography sx={{...headerCell, textAlign: "right"}}>Hrs/unit</Typography>
                            <Typography sx={{...headerCell, textAlign: "right"}}>Overtime</Typography>
                            <Typography sx={headerCell}>Status</Typography>
                            <Typography sx={{...headerCell, textAlign: "right"}}>People</Typography>
                            <Typography sx={headerCell}/>
                        </Box>

                        {steps.map((one, index) => (
                            <Box key={one.USID}
                                 sx={{display: "grid", gridTemplateColumns: COLUMNS, gap: "12px",
                                      padding: "10px 14px", alignItems: "center",
                                      borderBottom: `1px solid ${Grey2}`,
                                      "&:last-of-type": {borderBottom: "none"},
                                      opacity: busy === one.USID ? 0.5 : 1}}>
                                <Typography sx={{fontSize: 12, color: Grey5}}>{index + 1}</Typography>
                                <Typography sx={{fontSize: 13, color: Grey17, overflow: "hidden",
                                                 textOverflow: "ellipsis", whiteSpace: "nowrap"}}
                                            title={one.Description ?? one.Name ?? ""}>
                                    {one.Name || "—"}
                                </Typography>
                                <Box>
                                    {/* THE PROVENANCE, AND IT IS THE POINT OF THIS COLUMN. Without
                                        it a plan that has drifted from the item's routing looks
                                        exactly like one that follows it. */}
                                    {one.FromRouting
                                        ? <Chip size="small" label="routing"
                                                sx={{height: 20, fontSize: 11}}/>
                                        : <Typography sx={{fontSize: 12, color: "#B26A00"}}>
                                              added by hand
                                          </Typography>}
                                </Box>
                                <Typography sx={{fontSize: 13, textAlign: "right",
                                                 fontVariantNumeric: "tabular-nums",
                                                 // UNMEASURED IS NOT ZERO. A step with no hours is
                                                 // not counted in the lead time at all, and showing
                                                 // "0" would say it takes no time.
                                                 color: one.HoursPerUnit == null
                                                        ? "#B26A00" : Grey17}}>
                                    {one.HoursPerUnit == null ? "not set" : hours(one.HoursPerUnit)}
                                </Typography>
                                <Typography sx={{fontSize: 13, color: Grey17, textAlign: "right",
                                                 fontVariantNumeric: "tabular-nums"}}>
                                    {hours(one.OvertimeHours)}
                                </Typography>
                                <Typography sx={{fontSize: 12, color: Grey7}}>
                                    {(one.Status || "").replace("_", " ").toLowerCase() || "—"}
                                </Typography>
                                <Box sx={{display: "flex", justifyContent: "flex-end",
                                          alignItems: "center", gap: "3px"}}>
                                    {!!one.People?.length && (
                                        <Tooltip title={one.People.map((p: any) =>
                                            p?.Name || p?.AccountUserUSID).join(", ")}>
                                            <Box sx={{display: "flex", alignItems: "center",
                                                      gap: "2px"}}>
                                                <PersonOutlined sx={{fontSize: 14, color: Grey7}}/>
                                                <Typography sx={{fontSize: 13, color: Grey17}}>
                                                    {one.People.length}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    )}
                                </Box>
                                <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                                    {editable && (
                                        <>
                                            <IconButton size="small" aria-label="Edit this step"
                                                        onClick={() => setStepDialog(one)}>
                                                <EditOutlined sx={{fontSize: 16}}/>
                                            </IconButton>
                                            <IconButton size="small" aria-label="Remove this step"
                                                        onClick={() => remove(one)}>
                                                <DeleteOutlined sx={{fontSize: 16}}/>
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* THE DIVERGENCE, SAID IN A SENTENCE. A count of rows with a marker is not the
                        same as being told the plan has drifted from the item's definition. */}
                    {!!divergence && (
                        <Typography sx={{fontSize: 13, marginTop: "10px", lineHeight: 1.6,
                                         color: divergence.byHand ? "#B26A00" : Grey7}}>
                            {divergence.byHand === 0
                                ? `Every step came from the item's routing.`
                                : divergence.from === 0
                                    ? `Every step was added by hand — this plan does not follow the `
                                      + `item's routing at all.`
                                    : `${divergence.from} of ${divergence.from + divergence.byHand} `
                                      + `steps came from the item's routing; `
                                      + `${divergence.byHand} ${divergence.byHand === 1
                                            ? "was" : "were"} added by hand.`}
                        </Typography>
                    )}
                </>
            )}

            {stepDialog && (
                <StepDialog step={stepDialog === "new" ? null : stepDialog}
                            revisionUSID={revisionUSID!}
                            onClose={() => setStepDialog(null)}
                            onSaved={() => { setStepDialog(null); load(); }}/>
            )}

            {termsDialog && (
                <TermsDialog terms={needsTerms ? null : terms}
                             revisionUSID={revisionUSID!}
                             hasSteps={!!steps?.length}
                             onClose={() => setTermsDialog(false)}
                             onSaved={() => { setTermsDialog(false); loadTerms(); load(); }}/>
            )}
        </Box>
    );
}

/**
 * The computed schedule, with every caveat that says how much to trust it.
 *
 * THE NUMBER IS LARGE AND THE CAVEATS ARE BESIDE IT, NOT UNDER THE FOLD. A lead time of 14 days
 * over a plan with four unmeasured steps is not a 14-day plan, and a reader who sees only the
 * number has been misled by something that looks exact.
 */
function ScheduleBlock({schedule}: {schedule: Schedule}) {
    const caveats: string[] = [];
    if (schedule.StepsWithoutHours) {
        caveats.push(`${schedule.StepsWithoutHours} step`
                     + `${schedule.StepsWithoutHours === 1 ? " has" : "s have"} no hours set, so `
                     + `${schedule.StepsWithoutHours === 1 ? "it is" : "they are"} not counted in `
                     + `this at all.`);
    }
    if (schedule.ShiftConfigured === false) {
        caveats.push("No shift pattern is configured, so the capacity per day is a default rather "
                     + "than this shop's.");
    }
    if (schedule.CalendarDaysNotWorkingDays) {
        caveats.push("Calendar days, not working days — there is no working calendar here, so a "
                     + "plan spanning a weekend or a holiday finishes later than this says.");
    }

    const meets = schedule.MeetsNeedByDate;

    return (
        <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "14px 16px"}}>
            <Box sx={{display: "flex", alignItems: "flex-start", gap: "32px", flexWrap: "wrap"}}>
                <Box>
                    <Typography sx={{fontSize: 11, color: Grey5, textTransform: "uppercase",
                                     letterSpacing: "0.04em"}}>
                        Lead time
                    </Typography>
                    <Typography sx={{fontSize: 26, fontWeight: 600, color: Blue3,
                                     lineHeight: 1.2, marginTop: "2px"}}>
                        {schedule.LeadTimeDays == null
                            ? "—"
                            : `${schedule.LeadTimeDays} day${schedule.LeadTimeDays === 1 ? "" : "s"}`}
                    </Typography>
                </Box>
                <Fact label="Starts" value={when(schedule.PlanStartDate)}/>
                <Fact label="Finishes" value={when(schedule.PlannedEndDate)}/>
                <Fact label="Needed by" value={when(schedule.NeedByDate)}/>
                {/* THREE ANSWERS, NOT TWO. No need-by date is not "meets the date" and not "misses
                    it" -- there is nothing to meet, and a green tick there would be a claim. */}
                <Box>
                    <Typography sx={{fontSize: 11, color: Grey5, textTransform: "uppercase",
                                     letterSpacing: "0.04em"}}>
                        Against the date
                    </Typography>
                    <Typography sx={{fontSize: 13, marginTop: "2px",
                                     color: meets == null ? Grey5
                                            : meets ? "#1E7B34" : "#B3261E"}}>
                        {meets == null
                            ? "no date set"
                            : meets
                                ? `meets it${schedule.SlackDays != null
                                             ? ` with ${schedule.SlackDays} day`
                                               + `${schedule.SlackDays === 1 ? "" : "s"} to spare`
                                             : ""}`
                                : `misses it${schedule.SlackDays != null
                                              ? ` by ${Math.abs(schedule.SlackDays)} day`
                                                + `${Math.abs(schedule.SlackDays) === 1 ? "" : "s"}`
                                              : ""}`}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{display: "flex", gap: "28px", flexWrap: "wrap", marginTop: "14px",
                      paddingTop: "12px", borderTop: `1px solid ${Grey2}`}}>
                <Fact label="Hours per unit" value={hours(schedule.HoursPerUnit)}/>
                <Fact label="Normal hours" value={hours(schedule.NormalHours)}/>
                {/* BESIDE THE LEAD TIME, NEVER INSIDE IT. Overtime is worked outside the shift
                    pattern, so it does not consume that pattern's capacity -- it costs more and
                    signals a tight plan. */}
                <Fact label="Overtime hours" value={hours(schedule.OvertimeHours)}/>
                <Fact label="Capacity per day" value={hours(schedule.CapacityHoursPerDay)}/>
                <Fact label="Steps counted"
                      value={`${(schedule.Steps ?? 0) - (schedule.StepsNotCounted ?? 0)} `
                             + `of ${schedule.Steps ?? 0}`}/>
            </Box>

            {!!caveats.length && (
                <Box sx={{marginTop: "12px", display: "flex", flexDirection: "column",
                          gap: "4px"}}>
                    {caveats.map(one => (
                        <Typography key={one} sx={{fontSize: 12, color: "#B26A00",
                                                   lineHeight: 1.5, maxWidth: 760}}>
                            {one}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    );
}

function Fact({label, value}: {label: string; value: React.ReactNode}) {
    return (
        <Box>
            <Typography sx={{fontSize: 11, color: Grey5, textTransform: "uppercase",
                             letterSpacing: "0.04em"}}>
                {label}
            </Typography>
            <Typography sx={{fontSize: 13, color: Grey17, marginTop: "2px"}}>{value}</Typography>
        </Box>
    );
}

/**
 * One step of the route.
 *
 * HOURS PER UNIT MAY BE LEFT EMPTY, and that is not the same as zero. A step with no hours is not
 * counted in the lead time at all and the schedule says how many such steps there are; a step at
 * zero hours is a step somebody has measured as instantaneous. The service keeps them apart, so
 * this sends the clear rather than a zero.
 */
function StepDialog({step, revisionUSID, onClose, onSaved}: {
    step: Step | null; revisionUSID: string; onClose: () => void; onSaved: () => void;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [name, setName] = useState(step?.Name ?? "");
    const [description, setDescription] = useState(step?.Description ?? "");
    const [perUnit, setPerUnit] = useState(step?.HoursPerUnit ?? "");
    const [overtime, setOvertime] = useState(step?.OvertimeHours ?? "");
    const [state, setState] = useState(step?.Status ?? "PLANNED");
    const [notes, setNotes] = useState(step?.Notes ?? "");
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const save = async () => {
        setSaving(true);
        setProblem(null);
        try {
            const body: Record<string, any> = {
                Name: name,
                Description: description || undefined,
                HoursPerUnit: String(perUnit).trim() === "" ? undefined : perUnit,
                OvertimeHours: String(overtime).trim() === "" ? undefined : overtime,
                Notes: notes || undefined,
            };
            if (step) {
                await api.put("t1/v1/build_plan_steps", {
                    ...body, USID: step.USID, Status: state,
                    // AN EMPTIED FIELD IS A DECISION. Without the clears, hours once set could
                    // never be taken back off a step, and the schedule would go on counting a
                    // number nobody stands behind.
                    ClearHoursPerUnit: String(perUnit).trim() === "" ? true : undefined,
                    ClearOvertime: String(overtime).trim() === "" ? true : undefined,
                });
            } else {
                // NO SortOrder. The service appends; sending 0 would put every new step at the
                // front of somebody's route.
                await api.post("t1/v1/build_plan_steps",
                               {...body, BuildPlanRevisionUSID: revisionUSID});
            }
            onSaved();
        } catch (caught: any) {
            setProblem(caught?.response?.data?.Cause ?? "That step could not be saved.");
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>{step ? "Change this step" : "Add a step"}</DialogTitle>
            <DialogContent>
                <Box sx={{display: "flex", flexDirection: "column", gap: "14px",
                          paddingTop: "4px"}}>
                    <TextField label="Name" size="small" fullWidth value={name}
                               onChange={event => setName(event.target.value)}/>
                    <TextField label="Description" size="small" fullWidth multiline minRows={2}
                               value={description}
                               onChange={event => setDescription(event.target.value)}/>
                    <Box sx={{display: "flex", gap: "12px"}}>
                        <TextField label="Hours per unit" size="small" value={perUnit} sx={{flex: 1}}
                                   onChange={event => setPerUnit(event.target.value)}
                                   helperText="Leave empty if nobody has measured it — that is not
                                               the same as zero."/>
                        <TextField label="Overtime hours" size="small" value={overtime}
                                   sx={{flex: 1}}
                                   onChange={event => setOvertime(event.target.value)}
                                   helperText="Worked outside the shift pattern, for the whole
                                               step."/>
                    </Box>
                    {step && (
                        <TextField label="Status" size="small" select fullWidth value={state}
                                   onChange={event => setState(event.target.value)}>
                            {STEP_STATUSES.map(one => (
                                <MenuItem key={one} value={one}>
                                    {one.replace("_", " ").toLowerCase()}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <TextField label="Notes" size="small" fullWidth value={notes}
                               onChange={event => setNotes(event.target.value)}/>
                    {problem && (
                        <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.5}}>
                            {problem}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose}>Cancel</Button>
                <Button size="small" variant="contained" disabled={!name.trim() || saving}
                        onClick={save}>
                    {saving ? "Saving…" : step ? "Save" : "Add"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/** The quantity and the dates every schedule answer depends on. */
function TermsDialog({terms, revisionUSID, hasSteps, onClose, onSaved}: {
    terms: Terms | null; revisionUSID: string; hasSteps: boolean; onClose: () => void; onSaved: () => void;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [amount, setAmount] = useState(terms?.Quantity ?? "");
    const [unit, setUnit] = useState(terms?.UnitOfMeasure ?? "");
    const [start, setStart] = useState(terms?.PlanStartDate ?? "");
    const [needBy, setNeedBy] = useState(terms?.NeedByDate ?? "");
    const [notes, setNotes] = useState(terms?.Notes ?? "");
    // THE ITEM THE PLAN BUILDS, and whether to copy its routing into the steps (2026-10-28): a
    // plan advanced from a quote that named no item has neither, and this is where both are set.
    const [item, setItem] = useState<string | null>(terms?.ItemObjectUSID ?? null);
    const [seed, setSeed] = useState(!hasSteps);
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const save = async () => {
        setSaving(true);
        setProblem(null);
        try {
            await api.put("t1/v1/build_plan_terms", {
                ItemObjectUSID: item || undefined,
                SeedStepsFromRouting: seed && !hasSteps && !!item ? true : undefined,
                BuildPlanRevisionUSID: revisionUSID,
                Quantity: amount,
                UnitOfMeasure: unit || undefined,
                PlanStartDate: start || undefined,
                NeedByDate: needBy || undefined,
                Notes: notes || undefined,
                ClearPlanStartDate: !start ? true : undefined,
                ClearNeedByDate: !needBy ? true : undefined,
            });
            onSaved();
        } catch (caught: any) {
            setProblem(caught?.response?.data?.Cause ?? "Those terms could not be saved.");
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>What this plan builds</DialogTitle>
            <DialogContent>
                <Box sx={{display: "flex", flexDirection: "column", gap: "14px",
                          paddingTop: "4px"}}>
                    <Box>
                        <Typography sx={{fontSize: 12, color: Grey7, paddingBottom: "4px"}}>Item built</Typography>
                        <FormObjectPicker label="Item built" classificationCode="Item" options={[]} many={false}
                                          value={item} onChange={next => setItem(next ? String(next) : null)}/>
                        {!hasSteps && (
                            <FormControlLabel sx={{marginTop: "4px"}}
                                control={<Checkbox size="small" checked={seed && !!item} disabled={!item}
                                                   onChange={event => setSeed(event.target.checked)}/>}
                                label={<Typography sx={{fontSize: 12.5, color: Grey17}}>Seed the steps from the item's routing</Typography>}/>
                        )}
                    </Box>
                    <Box sx={{display: "flex", gap: "12px"}}>
                        <TextField label="Quantity" size="small" value={amount} sx={{flex: 1}}
                                   onChange={event => setAmount(event.target.value)}
                                   helperText="Every step's hours are per unit, and this is what
                                               multiplies them."/>
                        <TextField label="Unit" size="small" value={unit} sx={{flex: 1}}
                                   onChange={event => setUnit(event.target.value)}/>
                    </Box>
                    <Box sx={{display: "flex", gap: "12px"}}>
                        <TextField label="Plan starts" size="small" type="date" sx={{flex: 1}}
                                   value={start ? String(start).slice(0, 10) : ""}
                                   onChange={event => setStart(event.target.value)}
                                   slotProps={{inputLabel: {shrink: true}}}
                                   helperText="Without it there is a lead time but no dates."/>
                        <TextField label="Needed by" size="small" type="date" sx={{flex: 1}}
                                   value={needBy ? String(needBy).slice(0, 10) : ""}
                                   onChange={event => setNeedBy(event.target.value)}
                                   slotProps={{inputLabel: {shrink: true}}}
                                   helperText="Leave empty and there is nothing to meet or miss."/>
                    </Box>
                    <TextField label="Notes" size="small" fullWidth multiline minRows={2}
                               value={notes} onChange={event => setNotes(event.target.value)}/>
                    {problem && (
                        <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.5}}>
                            {problem}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose}>Cancel</Button>
                <Button size="small" variant="contained"
                        disabled={!String(amount).trim() || saving} onClick={save}>
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
